import { axiosClient } from '../../clients/axios-client'
import type { FileInfoType, FileParsingStatus, RlcpFile, SelectedRlcpFile } from '../../types'
import type {
  RlcpContentDto,
  RlcpFilesResponseDto,
  RlcpStructureItemDto,
  RlcpUploadSuccessResponseDto,
} from './dtos/rlcp'
import { RlcpStructureItemDtoSchema, RlcpUploadSuccessResponseDtoSchema } from './dtos/rlcp'

function getBraveFilesTree(file: RlcpContentDto): RlcpFile {
  const {
    name__v,
    id,
    children,
    document_id,
    major_version,
    minor_version,
    version_id,
    created_date,
    modified_date,
    document_url,
  } = file

  return {
    id: id,
    name: name__v,
    children: children.map((child: RlcpContentDto) => getBraveFilesTree(child)),
    documentId: document_id?.toString() ?? undefined,
    version:
      major_version !== undefined && minor_version !== undefined
        ? `${major_version}.${minor_version}`
        : undefined,
    versionId: version_id ?? undefined,
    createdAt: created_date ?? undefined,
    modifiedAt: modified_date ?? undefined,
    documentUrl: document_url ?? undefined,
  }
}

export async function getRlcpStructure(reportNumber: string) {
  try {
    const { data } = await axiosClient.get<RlcpFilesResponseDto>(`/rlcp/${reportNumber}/structure`)

    // We do not expect this to happen but would want to track it
    if (data.length > 1) {
      console.warn('Multiple content plans found')
    }

    if (data.length === 0) {
      // This should not happen, we either get an error or a content plan
      throw new Error('Report number valid but no content plans available')
    } else {
      const parsedData: RlcpStructureItemDto = RlcpStructureItemDtoSchema.parse(data[0])
      const response: RlcpFile = getBraveFilesTree(parsedData.content_plan)
      return response
    }
  } catch (error) {
    console.error('Error getting RLCP file structure', error)
    throw error
  }
}

export async function uploadRlcpFiles(files: SelectedRlcpFile[]) {
  try {
    const { data } = await axiosClient.post<RlcpUploadSuccessResponseDto>('/rlcp/parse/multi', {
      documents: files.map((file) => {
        return {
          version_id: file.versionId,
          filename: file.name,
        }
      }),
    })
    const parsedData = RlcpUploadSuccessResponseDtoSchema.parse(data)

    return parsedData.results.map((file) => {
      const { file_id, filename, file_type, parsing_status, uploaded_at, expires_at, is_excluded } =
        file.file
      const response: FileInfoType = {
        id: file_id,
        name: filename,
        type: file_type,
        parsingStatus: parsing_status as FileParsingStatus,
        uploadedAt: uploaded_at,
        expiresAt: expires_at,
        isExcluded: is_excluded ?? false,
      }
      return response
    })
  } catch (error) {
    console.error('Error uploading RLCP files', error)
    throw error
  }
}
